import Tooltip from '@mui/material/Tooltip';
import makeStyles from '@mui/styles/makeStyles';
import NRColoredBarStyles from './NRColoredBar.styles';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { capitalizeFirstLetter, round } from '../../utils/utils';
import { capitalizeFirstWord } from '../../utils/utils';
import NRSecondaryLink from '../NRSecondaryLink/NRSecondaryLink';
import ColoredCircle from '../../NRIcons/ColoredCircle';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

const useStyles = makeStyles(NRColoredBarStyles);

const ColoredBarTooltipContent = ({ title, color, value, showPercentage = false, total, capitalizeFirstOnly, capitalizeEachWord }) => {
  const classes = useStyles();
  return (
    <>
      {!!title && !!color && !!value ? (
        <div className={classes.tooltipContainer}>
          <ColoredCircle color={color} className={classes.marginRight} />
          <div className={classes.marginRight}>
            {`${capitalizeEachWord ? capitalizeFirstWord(title) : capitalizeFirstOnly ? capitalizeFirstLetter(title) : title}: ${value}`}{' '}
            {`${showPercentage ? '(' + round((value / total) * 100, 1) + '%)' : ''}`}
          </div>
        </div>
      ) : (
        ''
      )}
    </>
  );
};

const NRColoredBar = ({
  innerKey,
  config,
  emptyBarColor,
  showTotal = true,
  showValues = true,
  handleOnTotalClick,
  showPercentage = false,
  height = 24,
  capitalizeFirstOnly = true,
  capitalizeEachWord = true,
  isLoading,
  summaryLabel
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const total = Array.isArray(config) && config.reduce((a, b) => a + b?.value, 0);
  const EMPTY_BAR_COLOR = emptyBarColor || theme.palette.noData.background;

  const a11ySummary = total
    ? `${summaryLabel || 'Results'} summary: ${config
        .map(item => {
          const percent = total ? round((item.value / total) * 100, 1) : 0;
          const label = item.title;
          return `${label} ${item.value} (${percent}%)`;
        })
        .join(', ')}. Total ${total}.`
    : `${summaryLabel || 'Results'} summary: No data available.`;

  return (
    <div className={classes.coloredBar} style={{ height }} role="group" aria-label={`Result distribution bar${total ? `, total ${total}` : ''}`}>
      <Typography variant="srOnly" id={`${innerKey}-summary`}>
        {a11ySummary}
      </Typography>
      <div className={classes.segmentsContainer}>
        {!!total && !isLoading ? (
          config.map(configItem => {
            const percent = total ? round((configItem.value / total) * 100, 1) : 0;
            const segmentLabel = `${configItem.title}: ${percent}% (${configItem.value})`;

            return (
              <NRColoredSegment
                key={`${innerKey}-${configItem.title}-${configItem.value}`}
                width={(configItem.value * 100) / total}
                color={configItem.color}
                value={configItem.value}
                tooltipTitle={
                  !!configItem?.toolTip ? (
                    configItem.toolTip
                  ) : (
                    <ColoredBarTooltipContent
                      title={configItem?.title}
                      color={configItem.color}
                      value={configItem.value}
                      showPercentage={showPercentage}
                      total={total}
                      capitalizeFirstOnly={capitalizeFirstOnly}
                      capitalizeEachWord={capitalizeEachWord}
                    />
                  )
                }
                showValues={showValues}
                handleOnClick={configItem?.action}
                ariaLabel={segmentLabel}
              />
            );
          })
        ) : (
          <NRColoredSegment width={100} color={EMPTY_BAR_COLOR} showValues={false} ariaLabel="No data available" />
        )}
      </div>
      {showTotal && (
        <div className={clsx(classes.valueContainer, classes.total)}>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <NRSecondaryLink value={total} handleOnClick={() => handleOnTotalClick()} aria-label={`Total value: ${total}`} />
          )}
        </div>
      )}
    </div>
  );
};

const NRColoredSegment = ({ width, color, value, tooltipTitle, showValues, valueDisplayWithThreshold = 24, handleOnClick, ariaLabel }) => {
  const mySegment = React.createRef();
  const [segmentWidth, setSegmentWidth] = useState();

  useEffect(() => {
    !!mySegment?.current?.offsetWidth && setSegmentWidth(mySegment?.current?.offsetWidth);
  }, [mySegment?.current?.offsetWidth]);

  const classes = useStyles();
  return value !== 0 ? (
    <Tooltip title={tooltipTitle || ''} placement={'top'}>
      <div
        ref={mySegment}
        onClick={() => handleOnClick && handleOnClick()}
        style={{ width: `${width}%`, backgroundColor: color }}
        className={clsx(classes.valueContainer, classes.segment, { [classes.cursorDefault]: !handleOnClick })}
        role="img"
        aria-label={ariaLabel}
      >
        {showValues && segmentWidth > valueDisplayWithThreshold && value}
      </div>
    </Tooltip>
  ) : (
    <></>
  );
};

export default NRColoredBar;
